import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/common/Navbar";
import "../../styles/profilePage/ProfilePage.css";
import "../../styles/common/roots.css";
import config from "../../config/config";

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [lessonsCompleted, setLessonsCompleted] = useState(0); // Add state for lessons completed
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Fetch the user info from localStorage on component mount
    const getDecodedToken = () => {
      const token = localStorage.getItem("token");
      if (!token) return null;

      try {
        return jwtDecode(token);
      } catch (error) {
        console.error("Invalid token:", error);
        return null;
      }
    };

    const decodedToken = getDecodedToken();
    if (decodedToken) {
      setUser(decodedToken); // Set the user data directly from the token
      fetchUserData(decodedToken.id); // Fetch user data from backend
      checkStreak(decodedToken.id); // Call streak check when profile loads
      fetchLessonsCompleted(decodedToken.id); // Fetch lessons completed
    } else {
      navigate("/"); // If no valid token, navigate to the homepage
    }
  }, [navigate]);

  // Fetch user data from backend and update state
  const fetchUserData = async (userId) => {
    try {
      const response = await fetch(
        `${config.API_URL}/api/user/user-profile/${userId}`
      );
      const data = await response.json();

      if (response.ok) {
        setUser((prevUser) => ({
          ...prevUser,
          username: data.username || prevUser.username,
          // email: data.email || prevUser.email, //? Removed for Data Security
          callSign: data.call_sign, // Correct mapping here
        }));
      } else {
        console.error("Failed to fetch user data:", data.message);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  // Fetch lessons completed from the backend
  const fetchLessonsCompleted = async (userId) => {
    try {
      const response = await fetch(
        `${config.API_URL}/api/lesson/user-lessons/${userId}`
      );
      const data = await response.json();

      if (response.ok) {
        // 🔹 Filter only lessons where status is "completed"
        const completedLessons = data.lessons.filter(
          (lesson) => lesson.status === "completed"
        );

        setLessonsCompleted(completedLessons.length); // Update the state with the count of completed lessons
      } else {
        console.error("Failed to fetch lessons:", data.message);
      }
    } catch (error) {
      console.error("Error fetching lessons:", error);
    }
  };

  // Check streak and update user state
  const checkStreak = async (userId) => {
    try {
      const response = await fetch(`${config.API_URL}/api/user/check-streak`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId }),
      });

      const data = await response.json();
      if (response.ok) {
        setUser((prevUser) => ({
          ...prevUser,
          loginStreak: data.loginStreak, // Update streak in state
        }));
      } else {
        console.error("Failed to update streak:", data.message);
      }
    } catch (error) {
      console.error("Error checking streak:", error);
    }
  };

  // Handle user logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null); // Clear state after logout
    navigate("/"); // Redirect to home page
  };

  // Ensure user is properly loaded before rendering
  if (!user) {
    return <h1>Loading...</h1>;
  }

  return (
    <div>
      <Navbar />
      <div className="container-fluid d-flex justify-content-center align-items-center mt-5">
        <div className="d-flex flex-column align-items-center">
          <div className="circle">
            <img
              src="profilepage-placeholder.jpg"
              alt="Profile"
              className="profileImage"
            />
          </div>
          <div className="user-info">
            {user.callSign ? (
              <div className="username mt-2 mt-lg-3">C/S {user.callSign}</div>
            ) : (
              <div className="username mt-2 mt-lg-3">{user.username}</div>
            )}
          </div>
          {/* //? Removed for Data Security */}
          {/* <div className="email mt-lg-1">{user.email}</div> */}
          <div className="d-flex justify-content-center mt-4">
            <div className="circle-container mx-2">
              <div className="circle-icon">
                <i className="fas fa-trophy"></i>
              </div>
              <div className="text-center">
                <div className="circle-number">{lessonsCompleted}</div>
                <div className="circle-description">Lessons completed</div>
              </div>
            </div>

            <div className="circle-container mx-2">
              <div className="circle-icon">
                <i className="fas fa-fire"></i>
              </div>
              <div className="text-center">
                <div className="circle-number">{user.loginStreak}</div>
                <div className="circle-description">Days on streak</div>
              </div>
            </div>
          </div>
          <div className="profileButtons mt-3 d-flex flex-column align-items-center">
            <button
              onClick={() => navigate("/profile/edit")}
              className="editProfile mb-3"
            >
              Edit Profile
            </button>

            <button
              onClick={() => navigate("/feedback")}
              className="editProfile mb-3"
            >
              Feedback
            </button>

            <button onClick={handleLogout} className="logout">
              <i className="fas fa-sign-out-alt"></i> Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
